import { useState } from 'react';
import type { HomeTone } from '@aithora/shared';
import type { OpCtx } from './OperatorHome';
import { Act, chip16, D, S } from './ui';
import { useCustomerText } from '../../ui/buildLabels';

// Monitoring (design T12234-12337, hOpLedger): everything the AI did, including what it
// refused, the rules protecting members and the sealed record. Reads the PROPOSED
// GET /athome/monitoring (the platform's own /oversight and /ledger routes are not
// scoped to at-home members yet).

const vColor = (v: string) => (v === 'BLOCKED' ? '#B3261E' : v === 'HELD' ? '#4A5866' : '#1F6B52');
const HEAD = 'flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#22272E;';
const SEE: Record<HomeTone, string> = { good: '#1F6B52', watch: '#5B6B7C', alert: '#B3261E', none: '#54606C' };
const WHY_DOT = (k: string) => 'flex:0 0 5px;width:5px;height:5px;border-radius:50%;background:' + (k.includes('TRIPPED') ? '#B3261E' : 'rgba(34,39,46,.3)') + ';';

export function OpMonitoring({ c }: { c: OpCtx }) {
  const plain = useCustomerText();
  const m = c.o.monitoring.data;
  const [openI, setOpenI] = useState<number | null>(null);
  const [ledOpen, setLedOpen] = useState(false);
  if (!m) {
    return (
      <D css="padding-top:8px;font-size:16px;color:#54606C;">
        {c.o.monitoring.error ? plain('Monitoring is not connected yet: waiting on GET /athome/monitoring. Nothing here means not wired, never nothing happened.') : ''}
      </D>
    );
  }
  const held = m.actions.filter((a) => a.verdict === 'HELD').length;
  const blocked = m.actions.filter((a) => a.verdict === 'BLOCKED').length;
  const pmax = 45;
  return (
    <D css="display:flex;flex-direction:column;gap:10px;min-width:0;">
      <D css={'display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:8px;min-width:0;padding:11px 14px;border-radius:11px;background:' + (blocked ? 'rgba(179,38,30,.07)' : 'rgba(47,138,111,.09)') + ';border:1px solid ' + (blocked ? 'rgba(179,38,30,.3)' : 'rgba(47,138,111,.28)') + ';'}>
        <S css={'flex:0 0 10px;width:10px;height:10px;border-radius:50%;background:' + (blocked ? '#B3261E' : '#12503C') + ';animation:' + (blocked ? 'pRed 1.8s ease-out infinite' : 'breathe 2.4s ease-in-out infinite') + ';'} />
        <S css={'flex:1 1 260px;min-width:0;font-size:19px;font-weight:700;letter-spacing:-.025em;line-height:1.25;color:' + (blocked ? '#B3261E' : '#12503C') + ';text-wrap:pretty;'}>
          {blocked ? `${blocked}${blocked === 1 ? ' request was blocked today. Nothing reached a member.' : ' requests were blocked today. Nothing reached a member.'}` : 'Nothing has been blocked today. Every check passed.'}
        </S>
        <S css="flex:0 1 220px;min-width:0;">
          <S css="display:flex;align-items:flex-end;gap:1px;height:34px;min-width:0;">
            {m.perMinute.map((v, i) => (
              <S key={i} title={`${m.perMinute.length - 1 - i}m ago · ${v} checks`} css={'flex:1 1 0;min-width:2px;height:' + Math.round((v / pmax) * 34) + 'px;border-radius:1px;transform-origin:bottom;background:' + (i === m.perMinute.length - 1 ? '#2F5C97' : 'rgba(47,92,151,.34)') + ';'} />
            ))}
          </S>
          <S css="display:block;font-size:14px;color:#54606C;margin-top:2px;">checks per minute {'·'} last 60 minutes {'·'} live</S>
        </S>
        <S css="flex:1 1 auto;min-width:0;display:flex;align-items:baseline;justify-content:flex-end;gap:18px;flex-wrap:wrap;row-gap:6px;">
          {([['checks run today', m.checksToday.toLocaleString('en-US'), '#22272E'], ['waiting on a person', String(held), held ? '#4A5866' : '#54606C'], ['blocked', String(blocked), blocked ? '#B3261E' : '#54606C']] as const).map(([k, v, col]) => (
            <S key={k} css="flex:0 0 auto;display:block;text-align:right;">
              <S css={'display:block;font-size:18px;font-weight:700;line-height:1;font-variant-numeric:tabular-nums;color:' + col + ';'}>{v}</S>
              <S css="display:block;font-size:14px;letter-spacing:.07em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:2px;">{k}</S>
            </S>
          ))}
        </S>
      </D>

      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:12px 26px;min-width:0;align-items:start;">
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
            <S css={HEAD}>Every action, in order</S>
            <S css="flex:1 1 140px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">newest first {'·'} tap a line to see which rules fired</S>
          </D>
          {m.actions.map((a, i) => (
            <S key={i} css="display:block;min-width:0;">
              <Act
                css={'display:flex;align-items:baseline;gap:9px;padding:3px 4px;border-bottom:1px solid rgba(34,39,46,.06);border-radius:5px;min-width:0;text-decoration:none;color:inherit;animation:slideIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 40 + 'ms;' + (openI === i ? 'background:rgba(47,92,151,.07);' : '')}
                onClick={() => setOpenI(openI === i ? null : i)}
              >
                <S css="flex:0 0 38px;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;">{a.at}</S>
                <S css={'flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:' + vColor(a.verdict) + ';' + (i === 0 ? 'animation:breathe 2s ease-in-out infinite;' : '')} />
                <S css="flex:1 1 0;min-width:0;font-size:14px;line-height:1.35;color:#22272E;text-wrap:pretty;">
                  <b style={{ fontWeight: 700 }}>{a.who.split(' ')[0]}</b> {'·'} {a.what}
                </S>
                <S css={chip16(vColor(a.verdict), vColor(a.verdict) + '1F')}>{a.verdict}</S>
              </Act>
              {openI === i && (
                <S css="display:flex;flex-wrap:wrap;gap:4px 16px;margin:2px 0 5px 47px;padding-left:9px;border-left:2px solid rgba(34,39,46,.14);min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);">
                  {a.why.map((w) => (
                    <S key={w} css={'display:inline-flex;align-items:center;gap:6px;font-size:14px;color:' + (w.includes('TRIPPED') ? '#B3261E' : '#54606C') + ';white-space:nowrap;'}>
                      <S css={WHY_DOT(w)} />
                      {w}
                    </S>
                  ))}
                </S>
              )}
            </S>
          ))}
          <D css="font-size:14px;color:#54606C;line-height:1.4;margin-top:5px;text-wrap:pretty;">Nothing is hidden here. If the AI did something, it is on this list, including the things it refused to do.{m.real ? '' : ' SAMPLE until the member feed is wired.'}</D>
        </D>

        <D css="display:flex;flex-direction:column;gap:11px;min-width:0;">
          <D css="min-width:0;">
            <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
              <S css={HEAD}>The rules protecting you</S>
              <S css="flex:1 1 130px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">how close each one is to being tested</S>
            </D>
            {m.rules.map((g, i) => {
              const col = g.pressurePct > 90 ? '#B3261E' : g.pressurePct > 70 ? '#4A5866' : '#1F6B52';
              return (
                <S key={g.rule} css="display:flex;align-items:center;gap:8px;padding:2px 0;min-width:0;">
                  <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{g.rule}</S>
                  <S css={'flex:0 0 auto;font-size:14px;color:' + (g.tripsToday ? '#4A5866' : '#54606C') + ';white-space:nowrap;'}>{g.tripsToday ? `${g.tripsToday} today` : 'none today'}</S>
                  <S css="flex:0 0 62px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;">
                    <S css={'display:block;height:100%;width:' + g.pressurePct + '%;border-radius:3px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:' + i * 55 + 'ms;background:' + (g.pressurePct > 90 ? '#B3261E' : g.pressurePct > 70 ? '#5B6B7C' : '#2F8A6F') + ';'} />
                  </S>
                  <S css={'flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + col + ';'}>{`${g.pressurePct}%`}</S>
                </S>
              );
            })}
            <D css="font-size:14px;color:#54606C;line-height:1.4;margin-top:4px;text-wrap:pretty;">Higher means the rule is being pushed harder today. At 100% it stops the AI and calls a person. None of these can be switched off from inside the app.</D>
          </D>
          <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
            <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
              <S css={HEAD}>What is watching</S>
              <S css="flex:1 1 130px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">the four things that would notice if something went wrong</S>
            </D>
            <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:3px 16px;min-width:0;">
              {m.watching.map((s) => (
                <S key={s.k} css="display:flex;align-items:baseline;gap:7px;padding:2px 0;min-width:0;">
                  <S css={'flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:' + SEE[s.tone] + ';'} />
                  <S css="flex:0 0 auto;font-size:14px;font-weight:600;">{s.k}</S>
                  <S css="flex:1 1 0;min-width:0;font-size:14px;color:#54606C;overflow-wrap:break-word;">{s.v}</S>
                </S>
              ))}
            </D>
          </D>
          <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
            <S css="display:flex;align-items:center;gap:9px;flex-wrap:wrap;min-width:0;">
              <S css={HEAD}>Tamper-proof record</S>
              <S css={chip16(m.ledger.state === 'VERIFIED' ? '#1F6B52' : '#B3261E', m.ledger.state === 'VERIFIED' ? 'rgba(47,138,111,.14)' : 'rgba(179,38,30,.12)')}>{m.ledger.state}</S>
              <S css="flex:1 1 0;min-width:0;" />
              <Act css="flex:0 0 auto;font-size:14px;font-weight:700;color:#264C7E;text-decoration:none;white-space:nowrap;" onClick={() => setLedOpen(!ledOpen)}>
                {ledOpen ? 'Hide the record' : 'Show the record'}
              </Act>
            </S>
            <S css="display:block;font-size:14px;color:#54606C;line-height:1.4;margin-top:3px;text-wrap:pretty;">{`Every action above is written to a sealed record that cannot be edited afterwards, not by us, not by your care team. Block ${m.ledger.block.toLocaleString('en-US')} · last sealed ${m.ledger.lastSealedAt}.`}</S>
            {ledOpen && (
              <S css="display:block;margin-top:6px;min-width:0;">
                {m.ledger.rows.map((b) => (
                  <S key={b.block} css="display:flex;align-items:baseline;gap:8px;padding:2px 0;border-top:1px solid rgba(34,39,46,.06);min-width:0;">
                    <S css="flex:0 0 auto;font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;">{b.block}</S>
                    <S css="flex:1 1 0;min-width:0;font-size:14px;color:#3C454F;overflow-wrap:break-word;">{b.what}</S>
                    <S css="flex:0 0 auto;font-variant-numeric:tabular-nums;font-size:14px;color:#1F6B52;">{b.hash}</S>
                  </S>
                ))}
              </S>
            )}
          </D>
        </D>
      </D>
    </D>
  );
}
